import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div className="fixed inset-0 flex flex-col items-center justify-center bg-surface-1 p-6 text-center animate-fadeIn">
                    <div className="bg-surface-2 p-8 rounded-2xl shadow-2xl border border-white/10 max-w-md w-full relative overflow-hidden">
                        {/* Background decoration */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary/10 rounded-full blur-2xl transform -translate-x-1/2 translate-y-1/2"></div>

                        <div className="relative z-10">
                            {/* Animated icon */}
                            <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary/20 animate-pulse">
                                <span className="material-symbols-outlined text-4xl text-primary">rocket_launch</span>
                            </div>

                            <h1 className="text-xl font-black text-text-primary mb-2 uppercase tracking-wide">Nouvelle version disponible !</h1>
                            <p className="text-text-secondary mb-6 text-sm leading-relaxed">
                                Une mise à jour de Padel Brothers vient d'être déployée. Rechargez pour profiter des dernières améliorations et corrections.
                            </p>

                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => window.location.reload()}
                                    className="w-full bg-gradient-to-r from-primary to-secondary text-white py-3.5 rounded-xl font-bold shadow-lg hover:shadow-primary/25 hover:scale-[1.02] transition-all flex items-center justify-center gap-2 group"
                                >
                                    <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform duration-300">arrow_forward</span>
                                    Continuer avec la version à jour
                                </button>

                                <a
                                    href="/"
                                    className="w-full bg-surface-3 text-text-primary py-3.5 rounded-xl font-medium hover:bg-surface-4 transition-colors flex items-center justify-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-sm">home</span>
                                    Retour à l'accueil
                                </a>
                            </div>

                            {(import.meta.env.DEV || window.location.hostname === 'localhost') && this.state.error && (
                                <div className="mt-8 text-left">
                                    <div className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-2 flex items-center gap-1">
                                        <span className="material-symbols-outlined text-xs">bug_report</span>
                                        Détails techniques (Dev only)
                                    </div>
                                    <pre className="p-4 bg-black/40 rounded-lg text-[10px] text-error font-mono overflow-x-auto whitespace-pre-wrap border border-white/5">
                                        {this.state.error.toString()}
                                        {this.state.error.stack && (
                                            <>
                                                {'\n\n'}
                                                {this.state.error.stack.split('\n').slice(0, 3).join('\n')}...
                                            </>
                                        )}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
